import { ErrorStatus } from '@/components/inputs';
import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { CongratulationScreen } from '@/global/components/CongratulationScreen';
import { DataSource, DataSourceAmazonS3Config, DataSourceFileConfig } from '@/models/dataSource';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { useSaveDataSourceMutation } from '@/services/dataSources';
import { FC, useEffect } from 'react';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';

type AmazonS3ConfigurationCompletedProps = {
  onInit?: VoidFunction;
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
  isModal?: boolean;
};

export const AmazonS3ConfigurationCompletedStep: FC<AmazonS3ConfigurationCompletedProps> = ({ onInit, onNextStep, onPrevStep, isModal }) => {
  const workspace = useWorkspace();
  const dispatch = useAppDispatch();

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceAmazonS3Config>> | undefined;
  const dataSourceType = dataSource.type;

  const [saveDataSource, { isLoading, error: dataSourceError, data: savedDataSource }] = useSaveDataSourceMutation();

  const completedStep = async () => {
    if (dataSource && dataSource.name && dataSource.configuration) {
      // headersFileKey is wizard-only (stashed by FileDataSourceColumnMapStep) and must not be sent to the API.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { headersFileKey, ...configuration } = dataSource.configuration as DataSourceAmazonS3Config & Pick<DataSourceFileConfig, 'headersFileKey'>;
      const response = await saveDataSource({
        workspaceId: workspace.id,
        dataSource: {
          type: dataSourceType.id,
          name: dataSource.name,
          configuration: {
            ...configuration,
            type: 'CUSTOMER',
          },
        },
      });

      if ('error' in response) {
        throw new Error((response as any).error);
      }
    }
  };

  useEffect(() => {
    if (savedDataSource) {
      dispatch(stashDataSource(savedDataSource.dataSource));
    }
  }, [savedDataSource]);

  useEffect(() => {
    completedStep();
    onInit();
  }, []);

  if (dataSourceError) {
    return <ErrorStatus error={(dataSourceError as any).error} />;
  }

  return dataSource && dataSource.name && dataSource.configuration ? (
    <Loadable isLoading={isLoading}>
      <CongratulationScreen
        title='data_source_edit.code.installation_success.data_source_created_succesfully'
        subtitle='data_source_edit.code.installation_success.data_source_created_succesfully_subtitle'
        primaryButton={
          isModal
            ? {
                text: 'data_source_edit.code.installation_success.choose_destination',
                onClick: onNextStep,
              }
            : {
                text: 'data_source_edit.code.installation_success.create_new_campaing',
                onClick: onNextStep,
              }
        }
        secondaryButton={
          isModal
            ? {
                text: 'data_source_edit.code.installation_success.add_another_data_source',
                onClick: onPrevStep,
              }
            : {
                text: 'data_source_edit.code.installation_success.view_all_data_sources',
                onClick: onPrevStep,
              }
        }
      />
    </Loadable>
  ) : null;
};
